import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import ModalDialog from "../common/ModalDialog";
import {
  applyCsvImport,
  createCsvImport,
  CsvImportJobDto,
  CsvSourceFormat,
  getCsvImport,
  isCsvImportPlan,
  isInFlight,
  MigrationEntity,
} from "../../api/migrationsApi";
import tailwindClassnames from "../common/TailwindClassnames";
import {
  buildCsvImportOptions,
  columnsFor,
  CsvColumnSpec,
  entityCopy,
  FORMAT_LABEL,
  templateFileName,
  templateHref,
} from "./csvImport";
import CsvImportReport from "./CsvImportReport";
import { uploadErrorMessage, useMigrationJob } from "./useMigrationJob";

/**
 * Import from CSV (AKL-813).
 *
 * Four stages behind one dialog: choose the format (and see the columns it expects, with a
 * template to download); for a Wildlife Insights file, decide whether this is really a full
 * migration; choose the file and let akili check it; review the plan and confirm. Nothing is
 * written until the confirm, and the copy says so at each stage.
 *
 * Entity-agnostic by construction — the sensors import is the first caller; the locations
 * and deployments imports (AKL-814/815) mount the same component with a different `entity`,
 * `formats` and, for project-scoped imports, a `targetProjectId`. Mount it only while open:
 * the format and step start fresh on each mount, and the poll stops when it unmounts.
 *
 * Errors from the upload and the apply are read in the shape the axios interceptor produces
 * (`err.data.detail`, a string or a 422 list), never `err.response`. The permission gate
 * lives in the caller: this dialog must not be mounted for a user without the entity's
 * create permission (`CREATE_SENSORS_IN_ORG` for sensors, `CREATE_DEPLOYMENTS_LOCATIONS` for
 * locations and deployments): the API's 403 would be a dead end for the user — a bundle
 * chosen and uploaded for nothing. (Until AKL-939 that 403 also signed the user out via the
 * interceptor; it no longer does, but the gate is still the right place to stop.)
 */

export interface ImportFromCsvProps {
  isOpen: boolean;
  /** The organisation the file belongs to. */
  orgId: number;
  /** What the file contains. Selects the columns, the copy and the entity sent to the API. */
  entity: MigrationEntity;
  /** The formats offered, first one preselected. One entry renders one (checked) radio. */
  formats: CsvSourceFormat[];
  /** The project, for the project-scoped imports of AKL-814/815. Omitted for sensors. */
  targetProjectId?: number | null;
  /**
   * Runtime columns the Akili format reads beyond the fixed ones — today the project's
   * deployment template (AKL-815, `schemaColumns`). Three states: `undefined` means not
   * applicable (sensors, locations) or not known yet (the project is still loading), and
   * renders nothing new; `[]` means the caller looked and found no readable template
   * columns — no template, or one whose properties a cell cannot hold — and renders the
   * "no template columns" line (the sentence is deliberately narrower than "no template",
   * since `schemaColumns` cannot tell those apart); a non-empty list renders as a second
   * column list and is appended to the downloadable template. Ignored for the CTMS format,
   * whose columns are Wildlife Insights' own.
   */
  extraColumns?: CsvColumnSpec[];
  /**
   * Whether the Wildlife Insights fork may offer "Migrate full export". The caller decides
   * from the user's permissions (`canMigrateFullExport` for an organisation-level import,
   * the target-mode `canOpenWildlifeInsightsMigration` for a project-scoped one); false
   * skips the fork entirely.
   */
  canMigrateFullExport: boolean;
  /**
   * Called on every way out of the dialog with the last job seen (or null), so the caller can
   * refresh what it shows when the user closes an import that was applying or applied.
   */
  onClose: (lastJob: CsvImportJobDto | null) => void;
  /** Called once when the job reaches "applied". */
  onImported: (job: CsvImportJobDto) => void;
  /** Called when the user chooses the full Wildlife Insights migration instead. */
  onMigrateFullExport: () => void;
  /**
   * Called when the user picks another file while the dialog had lost track of a job the
   * server may still be working on. The dialog forgets that job, so the caller is the only
   * one left who can follow it to completion and refresh what it shows (PR #761 review).
   * Required, like `onClose`, so a future caller (AKL-814/815) cannot forget the duty.
   */
  onJobAbandoned: (job: CsvImportJobDto) => void;
}

type Step = "format" | "fork" | "file";

const ImportFromCsv: React.FC<ImportFromCsvProps> = ({
  isOpen,
  orgId,
  entity,
  formats,
  targetProjectId,
  extraColumns,
  canMigrateFullExport,
  onClose,
  onImported,
  onMigrateFullExport,
  onJobAbandoned,
}) => {
  const { t } = useTranslation();
  const copy = entityCopy(entity);
  const titleId = useId();

  const [format, setFormat] = useState<CsvSourceFormat>(formats[0]);
  const [step, setStep] = useState<Step>("format");
  const [file, setFile] = useState<File | null>(null);

  const {
    job,
    setJob,
    busy,
    setBusy,
    error,
    setError,
    pollFailure,
    apply,
    reset,
    working,
  } = useMigrationJob<CsvImportJobDto>({
    enabled: isOpen,
    fetchJob: getCsvImport,
    applyJob: applyCsvImport,
    onApplied: onImported,
    pollFailureText: t(copy.pollFailure),
    applyFailureText: t(copy.applyFailure),
  });

  if (!isOpen) return null;

  const close = () => onClose(job);

  const handleNext = () => {
    // A Wildlife Insights cameras.csv usually comes out of a full export, so ask whether
    // this is really a full migration — but only when the user could run one, and only for
    // an entity that has fork copy to show. The fork-strings half is type-level safety: no
    // registered entity has a `ctms_csv` column spec without fork strings (a locations
    // modal never offers that format, and `columnsFor` would throw first if it did), so it
    // has no reachable test. The sensors fork test covers the true branch; the locations
    // test covers "a non-CTMS format never forks".
    const canFork =
      format === "ctms_csv" &&
      canMigrateFullExport &&
      Boolean(copy.forkQuestion) &&
      Boolean(copy.forkImportOnly);
    setStep(canFork ? "fork" : "file");
  };

  const handleBack = () => {
    setStep("format");
    setFile(null);
    setError(null);
  };

  const handleCheck = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createCsvImport(
        file,
        buildCsvImportOptions(orgId, entity, format, targetProjectId),
      );
      setJob(created);
    } catch (err) {
      setError(
        uploadErrorMessage(err, {
          unreadable: t("akili could not read that file."),
          inProgress: t(
            "You already have a migration in progress. Wait for it to finish.",
          ),
          retry: t("akili could not check this file. Try again in a moment."),
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  // Back to the file step with the format kept: the user is fixing the file, not the choice.
  // When polling gave up on a job that was still in flight, the server may finish it after
  // this dialog has forgotten it, so the job is handed to the caller first rather than
  // silently dropped with the reset.
  const handleChooseAnother = () => {
    if (job !== null && isInFlight(job.status)) {
      onJobAbandoned(job);
    }
    reset();
    setFile(null);
    setStep("file");
  };

  const columns = columnsFor(entity, format);
  // Template columns belong to the Akili layout only; the CTMS layout is WI's own header.
  const templateColumns = format === "akili_csv" ? (extraColumns ?? []) : [];
  const showTemplateColumns =
    format === "akili_csv" && extraColumns !== undefined;
  const readablePlan = isCsvImportPlan(job?.plan) ? (job?.plan ?? null) : null;
  const readableResult = isCsvImportPlan(job?.result)
    ? (job?.result ?? null)
    : null;
  const planBlocked = readablePlan !== null && readablePlan.blocking.length > 0;
  const canApply =
    job?.status === "planned" && readablePlan !== null && !planBlocked && !busy;
  // Also offered when polling gave up: a closed dialog cannot be reopened to the same job,
  // so without it the footer would leave the user only Cancel.
  const offerAnotherFile =
    job?.status === "failed" ||
    (job?.status === "planned" && planBlocked) ||
    pollFailure !== null;

  return (
    <ModalDialog
      onClose={close}
      busy={busy}
      labelledBy={titleId}
      overlayClassName="z-50"
      panelClassName="max-w-2xl"
    >
      <>
        <div className={tailwindClassnames.modalHeaderRow}>
          <h2 id={titleId} className={tailwindClassnames.modalTitle}>
            {t(copy.title)}
          </h2>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={close}
            aria-label={t("Close")}
            disabled={busy}
            data-testid="csv-import-close"
          >
            &times;
          </button>
        </div>

        <div className={`${tailwindClassnames.modalBody} space-y-4`}>
          {job === null && step === "format" && (
            <>
              <fieldset>
                <legend className={tailwindClassnames.basicSubHeading}>
                  {t("Format")}
                </legend>
                <div className="mt-1.5 space-y-2">
                  {formats.map((candidate) => {
                    const id = `csv-format-${candidate}`;
                    return (
                      <div key={candidate} className="flex items-start gap-2">
                        <input
                          id={id}
                          type="radio"
                          name="csv-format"
                          className={tailwindClassnames.checkBox}
                          checked={format === candidate}
                          onChange={() => setFormat(candidate)}
                          data-testid={id}
                        />
                        <label
                          htmlFor={id}
                          className={tailwindClassnames.checkBoxLabel}
                        >
                          {t(FORMAT_LABEL[candidate])}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>

              <div>
                <p className={tailwindClassnames.helperText}>
                  {t("Expected columns")} —{" "}
                  {t("Required columns are marked with *")}
                </p>
                <ul
                  className="mt-1.5 space-y-0.5"
                  data-testid="csv-expected-columns"
                >
                  {columns.map((column) => (
                    <li
                      key={column.name}
                      className={tailwindClassnames.helperText}
                    >
                      <code>{column.name}</code>
                      {column.required ? "*" : ""}
                      {column.hint && (
                        <span className="ml-2 italic">
                          {t(column.hint, column.hintParams)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {/* AKL-815: the columns this project's deployment template adds, or the
                    fact that none can be read. Nothing at all when the caller does not know
                    yet (`undefined`), so the modal never says "no template columns" about a
                    project that is still loading. */}
                {showTemplateColumns && templateColumns.length > 0 && (
                  <>
                    <p className={`${tailwindClassnames.helperText} mt-3`}>
                      {t("Columns from this project's deployment template")}
                    </p>
                    <ul
                      className="mt-1.5 space-y-0.5"
                      data-testid="csv-template-columns"
                    >
                      {templateColumns.map((column) => (
                        <li
                          key={column.name}
                          className={tailwindClassnames.helperText}
                        >
                          <code>{column.name}</code>
                          {column.hint && (
                            <span className="ml-2 italic">
                              {t(column.hint, column.hintParams)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {showTemplateColumns && templateColumns.length === 0 && (
                  <p
                    className={`${tailwindClassnames.helperText} mt-3`}
                    data-testid="csv-no-template"
                  >
                    {t(
                      "No deployment-template columns can be read for this project, so only the standard columns are read. Pick a template on the Project Details tab to import template fields.",
                    )}
                  </p>
                )}
                {/* A data: URL of the header row — no library, no object URL to revoke. */}
                <a
                  href={templateHref(entity, format, templateColumns)}
                  download={templateFileName(entity, format)}
                  className="mt-2 inline-block rounded text-sm text-storm-blue underline transition duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue dark:text-sky-blue"
                  data-testid="csv-template-download"
                >
                  {t("Download template")}
                </a>
              </div>
            </>
          )}

          {job === null && step === "fork" && (
            <>
              <p className={tailwindClassnames.basicLabel}>
                {copy.forkQuestion ? t(copy.forkQuestion) : null}
              </p>
              <p className={tailwindClassnames.helperText}>
                {t(
                  "A full migration also brings across the project, deployments and identifications from the export bundle.",
                )}
              </p>
              <div className="flex flex-wrap gap-2.5">
                <button
                  type="button"
                  className={tailwindClassnames.btnPrimary}
                  onClick={() => setStep("file")}
                  data-testid={`csv-import-${entity}-only`}
                >
                  {copy.forkImportOnly ? t(copy.forkImportOnly) : null}
                </button>
                <button
                  type="button"
                  className={tailwindClassnames.btnSecondary}
                  onClick={onMigrateFullExport}
                  data-testid="csv-migrate-full-export"
                >
                  {t("Migrate full export")}
                </button>
              </div>
            </>
          )}

          {job === null && step === "file" && (
            <div>
              <label
                className={tailwindClassnames.textInputLabel}
                htmlFor="csv-import-file"
              >
                {t("Choose a CSV file")}
              </label>
              <input
                id="csv-import-file"
                type="file"
                accept=".csv"
                className={tailwindClassnames.detailsPageTextInput}
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                disabled={busy}
                data-testid="csv-import-file"
              />
              <p className={`${tailwindClassnames.helperText} mt-1.5`}>
                {t("Nothing is saved until you review the check and confirm.")}
              </p>
            </div>
          )}

          {job !== null && working && (
            <div className="flex items-center gap-3 py-2">
              <BeatLoader size={8} color="#38bdf8" />
              <span className={tailwindClassnames.helperText}>
                {job.status === "applying"
                  ? t(copy.applying)
                  : t("Reading your file…")}
              </span>
            </div>
          )}

          {pollFailure && (
            <p className={tailwindClassnames.warningLabel}>{pollFailure}</p>
          )}

          {job?.status === "failed" && (
            <div role="alert">
              <p className={tailwindClassnames.warningHeader}>
                {t("This import failed")}
              </p>
              <p className={tailwindClassnames.helperText}>{job.error}</p>
            </div>
          )}

          {job?.status === "planned" && (
            <>
              <p className={tailwindClassnames.helperText}>
                {t(
                  "Nothing has been saved yet. Review this and confirm below.",
                )}
              </p>
              {readablePlan ? (
                <CsvImportReport report={readablePlan} />
              ) : (
                // Server/web version skew: the plan is not a shape this dialog can read.
                <div role="alert" className={tailwindClassnames.warningLabel}>
                  {t("akili could not read this report.")}
                </div>
              )}
            </>
          )}

          {job?.status === "applied" && (
            <>
              <p className={tailwindClassnames.basicLabel}>
                {t("Import complete.")}
              </p>
              {readableResult ? (
                <CsvImportReport report={readableResult} applied />
              ) : (
                <div role="alert" className={tailwindClassnames.warningLabel}>
                  {t("akili could not read this report.")}
                </div>
              )}
            </>
          )}

          {error && (
            <div role="alert" className={tailwindClassnames.warningLabel}>
              {error}
            </div>
          )}
        </div>

        <div className={tailwindClassnames.modalFooter}>
          {job === null && step !== "format" && (
            <button
              type="button"
              className={tailwindClassnames.btnGhost}
              onClick={handleBack}
              disabled={busy}
              data-testid="csv-import-back"
            >
              {t("Back")}
            </button>
          )}

          {job?.status === "applied" ? (
            <button
              type="button"
              className={tailwindClassnames.btnPrimary}
              onClick={close}
              data-testid="csv-import-done"
            >
              {t("Done")}
            </button>
          ) : (
            <button
              type="button"
              className={tailwindClassnames.btnSecondary}
              onClick={close}
              disabled={busy}
              data-testid="csv-import-cancel"
            >
              {t("Cancel")}
            </button>
          )}

          {job === null && step === "format" && (
            <button
              type="button"
              className={tailwindClassnames.btnPrimary}
              onClick={handleNext}
              data-testid="csv-import-next"
            >
              {t("Next")}
            </button>
          )}

          {job === null && step === "file" && (
            <button
              type="button"
              className={tailwindClassnames.btnPrimary}
              onClick={handleCheck}
              disabled={!file || busy}
              data-testid="csv-import-check"
            >
              {t("Check this file")}
            </button>
          )}

          {offerAnotherFile && (
            <button
              type="button"
              className={tailwindClassnames.btnSecondary}
              onClick={handleChooseAnother}
              disabled={busy}
              data-testid="csv-import-choose-another"
            >
              {t("Choose another file")}
            </button>
          )}

          {job?.status === "planned" && (
            <button
              type="button"
              className={tailwindClassnames.btnPrimary}
              onClick={apply}
              disabled={!canApply}
              data-testid="csv-import-apply"
            >
              {t(copy.confirm)}
            </button>
          )}
        </div>
      </>
    </ModalDialog>
  );
};

export default ImportFromCsv;
